import { formatLocalDate, parseDateString, toOrgDateOnly } from "@/utils/dateUtils";
import { computeSettlingInEndDate } from "./settlingInEndDate";

export interface AllocationPrefill {
  /** yyyy-MM-dd, from the allocation's from_date. */
  startDate: string;
  /** yyyy-MM-dd, computeSettlingInEndDate(startDate). */
  endDate: string;
  /** The allocation's contract version, only when it is among the ACTIVE/UPCOMING options. */
  contractId?: string;
  /** The allocation's group, only when it exists in the loaded groups. */
  groupId?: string;
  /** group.childcare_id, else (org-wide group) the effective contract's childcareId. */
  childcareId?: string;
}

/**
 * D2 (#1685): what the settling-in dialog takes from the child's first allocation.
 *
 * Pure. Returns null when the allocation's from_date does not reduce to a valid
 * calendar day. The childcare falls back to the effective contract's childcare
 * (the allocation's contract when offered, else the one already in the form) when
 * the group is org-wide.
 */
export function buildAllocationPrefill(input: {
  first: { from_date?: string; contract_id?: string; group_id?: string };
  contractOptionIds: readonly string[];
  contracts: readonly { id?: string; childcareId?: string }[];
  groups: readonly { id?: string; childcare_id?: string | null }[];
  /** Contract already in the form; used for the childcare fallback when the allocation's is not offered. */
  currentContractId: string;
}): AllocationPrefill | null {
  const { first, contractOptionIds, contracts, groups, currentContractId } = input;

  const start = parseDateString(toOrgDateOnly(first.from_date));
  if (!start) return null;

  const contractId =
    first.contract_id && contractOptionIds.includes(first.contract_id)
      ? first.contract_id
      : undefined;
  const group = first.group_id ? groups.find((g) => g.id === first.group_id) : undefined;

  let childcareId: string | undefined;
  if (group) {
    const effectiveContractId = contractId ?? currentContractId;
    childcareId =
      group.childcare_id ||
      contracts.find((c) => c.id === effectiveContractId)?.childcareId ||
      undefined;
  }

  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(computeSettlingInEndDate(start)),
    contractId,
    groupId: group?.id,
    childcareId,
  };
}
